<template>
  <v-col cols="12" class="pa-0">
    <v-col cols="12" class="px-3 py-0">
      <v-btn color="primary" icon @click="handleBack">
        <v-icon>mdi-arrow-left</v-icon>
      </v-btn>
      <span class="text-h6 mr-2" style="vertical-align: middle">
        {{ $t('message.pages.video.metricsPage.retention') }}
      </span>
    </v-col>
    <v-col cols="12" class="mb-1 px-5">
      <v-card flat>
        <span>
          {{ $t('message.pages.video.metricsPage.retentionMessage') }}
          <a target="__blank" :href="retentionLink">
            {{ $t('message.pages.video.metricsPage.readMore') }}
          </a>
          <br />
        </span>
      </v-card>
    </v-col>
    <v-col v-if="!errors.fetch">
      <v-col cols="12" class="datepicker-container pa-0 mb-1 px-3">
        <v-row align="center">
          <v-col cols="6">
            <date-picker
              :shortcuts="shortcuts"
              :clearable="false"
              :disabled-date="verifyDisabledDate"
              :max="10"
              @change="updateSelect"
              style="width: 100%"
              v-model="dateRange"
              confirm
              range
            >
              <template v-slot:input>
                <v-text-field
                  :label="$t('message.date')"
                  dense
                  :value="dateRangeValue"
                  outlined
                  prepend-inner-icon="mdi-calendar"
                  readonly
                  hide-details
                ></v-text-field>
              </template>
              <template v-slot:icon-calendar>
                <v-icon style="display: none">mdi-calendar</v-icon>
              </template>
            </date-picker>
          </v-col>
          <v-col cols="6">
            <v-card outlined class="py-4 px-6">
              <v-row>
                <span class="text-caption text--secondary">
                  {{ $t('message.pages.video.metricsPage.engagementCalc') }}

                  <v-tooltip top>
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon v-bind="attrs" v-on="on" size="20">
                        mdi-help-circle-outline
                      </v-icon>
                    </template>
                    <p style="width: 200px !important; margin-bottom: 0">
                      {{
                        $t('message.pages.video.metricsPage.engagementMessage')
                      }}
                    </p>
                  </v-tooltip>
                </span>
              </v-row>
              <v-row>
                <span class="text-h5 primary--text" v-if="!loadings.fetch">
                  {{ engagementCalc.toFixed(1) }}%
                </span>
              </v-row>
            </v-card>
          </v-col>
        </v-row>
      </v-col>
    </v-col>
    <v-container class="pa-0" style="margin-top: -4px">
      <v-card flat v-if="loadings.fetch" class="px-3">
        <loading-logo style="height: 265px" />
      </v-card>
      <v-card flat v-else-if="errors.fetch" class="mt-5 mb-8 px-3">
        <ad-block-error-message
          v-if="isAdBlockEnabled"
          :isLoading="loadings.fetch"
          @handleFetchData="fetchData"
        />
        <v-row justify="center" no-gutters v-else>
          <v-col cols="12" class="py-0 d-flex justify-center">
            <span>
              {{ $t('message.pages.video.metricsPage.error') }}
            </span>
          </v-col>
          <v-col cols="12" class="py-0 d-flex justify-center">
            <v-btn class="mt-3" color="primary" @click="fetchData">{{
              $t('message.pages.video.metricsPage.tryAgain')
            }}</v-btn>
          </v-col>
        </v-row>
      </v-card>
      <v-container class="pa-0 chart-container" v-else>
        <apexchart
          :class="{ darkThemeSvgFix: $vuetify.theme.dark }"
          ref="realtimechart"
          class="the-retention-chart"
          type="area"
          height="370"
          :options="retentionChartOptions"
          :series="retentionChartSeries"
        ></apexchart>
        <span
          :class="`video-chart-container ${
            chartOption === $t('message.pages.video.metricsPage.viewsPerTime')
              ? 'views-data-container'
              : ''
          }`"
          ref="videoContainer"
        >
          <div style="position: relative">
            <video id="video" class="video-box" paused></video>
          </div>
        </span>
      </v-container>
    </v-container>
    <v-col cols="12" class="pt-0 px-5" v-if="!loadings.fetch">
      <small class="text--secondary">{{
        $t('message.pages.video.metricsPage.updatedAtEachSec', {
          time: updateAnalyticsTime.time,
        })
      }}</small>
    </v-col>
  </v-col>
</template>

<script>
import VueApexCharts from 'vue-apexcharts'
import analyticsApi from '@/services/analyticsApi'
import { addSeconds, isAfter, isBefore, format, subDays } from 'date-fns'
import LoadingLogo from '../../../components/LoadingLogo.vue'
import { mapState, mapMutations } from 'vuex'
import i18n from '@/translations/lang'
import realtimeAnalyticsMixin from '@/utils/realtimeAnalytics.mixin.js'
import Hls from 'hls.js'
import DatePicker from 'vue2-datepicker'
import linkHelper from '@/utils/linkHelper.mixin.js'
import AdBlockErrorMessage from '@/components/AdBlockErrorMessage.vue'

export default {
  name: 'VideoRetention',
  components: {
    apexchart: VueApexCharts,
    LoadingLogo,
    DatePicker,
    AdBlockErrorMessage,
  },
  props: ['handleBack', 'customVideoId', 'videoPlayer', 'videoHls'],
  mixins: [realtimeAnalyticsMixin, linkHelper],
  data() {
    let _this = this
    return {
      isBeta: false,
      lastVideoFrameTime: 0,
      updateInterval: {},
      subMenuIndex: 0,
      playerReady: false,
      chartOptionTypes: [
        this.$t('message.pages.video.metricsPage.retention'),
        this.$t('message.pages.video.metricsPage.viewsPerTime'),
      ],
      chartOption: this.$t('message.pages.video.metricsPage.retention'),
      totalData: {
        views: 0,
        uniqueViews: 0,
        plays: 0,
        playRate: 0,
        uniquePlays: 0,
        retention: null,
      },
      dateRange: [],
      datePickerOpen: false,
      loadings: {
        fetch: true,
      },
      errors: {
        fetch: false,
      },
      maxValue: 0,
      retentionChartOptions: {
        chart: {
          events: {
            mouseMove: function (event, chartContext, configSet) {
              let { dataPointIndex, config } = configSet
              if (dataPointIndex < 0) dataPointIndex = _this.lastVideoFrameTime
              let currentXaxis = config.series[0].data[dataPointIndex]?.x ?? 0

              _this.setVideoCurrentTime(Number(currentXaxis))
              _this.lastVideoFrameTime = dataPointIndex
            },
          },
          toolbar: false,
          type: 'area',
          height: 350,
          width: '100%',
          offsetX: 0,
          offsetY: 0,
        },
        dataLabels: {
          enabled: false,
        },
        xaxis: {
          tickAmount: 4,
          labels: {
            rotate: 0,
            formatter: (val) => {
              if (val) {
                if (val > 3600)
                  return format(
                    addSeconds(new Date('2022/01/01 00:00'), val),
                    'HH:mm:ss'
                  )
                return format(
                  addSeconds(new Date('2022/01/01 00:00'), val),
                  'mm:ss'
                )
              } else {
                return format(new Date('2022/01/01 00:00'), 'mm:ss')
              }
            },
          },
        },
        tooltip: {
          theme: 'dark',
          enabled: true,
          y: {
            formatter: (val) => {
              return `${val} / ${
                ((val * 100) / _this.maxValue).toFixed(0) + '%'
              }`
            },
          },
        },
        stroke: {
          curve: 'straight',
        },
      },
      retentionChartSeries: [
        {
          name: i18n.t('message.pages.video.metricsPage.retentionRate'),
          data: [],
        },
      ],
      shortcuts: [
        {
          text: i18n.t('message.pages.video.metricsPage.datepicker.today'),
          onClick() {
            const date = new Date()
            return [date, date]
          },
        },
        {
          text: i18n.t('message.pages.video.metricsPage.datepicker.yesterday'),
          onClick() {
            const date = new Date()

            return [subDays(date, 1), date]
          },
        },
        {
          text: i18n.t('message.pages.video.metricsPage.datepicker.7days'),
          onClick() {
            const date = new Date()

            return [subDays(date, 7), date]
          },
        },
      ],
    }
  },
  async created() {
    this.dateRange = [subDays(new Date(), 7), new Date()]
  },
  async mounted() {
    this.$root.$emit('checkAdBlock')
    this.$vuetify.theme.dark
      ? (this.retentionChartOptions.tooltip.theme = 'dark')
      : (this.retentionChartOptions.tooltip.theme = 'light')
    this.isBeta = location.search.match('beta')
    await this.fetchData()
    this.changeUpdateTime()
    this.videoGraph = document.getElementById('video')
    if (Hls.isSupported()) {
      let hls = new Hls()
      hls.testBandwidth = false
      hls.startLevel = 0
      hls.loadLevel = hls.startLevel
      hls.loadSource(this.videoHls)

      hls.attachMedia(this.videoGraph)
      hls.on(
        Hls.Events.MANIFEST_PARSED,
        function () {
          //ERROR
          this.videoGraph.pause()
        }.bind(this)
      )
    } else if (this.videoGraph.canPlayType('application/vnd.apple.mpegurl')) {
      this.videoGraph.src = this.videoHls
      this.videoGraph.addEventListener(
        'canplay',
        function () {
          this.videoGraph.pause()
        }.bind(this)
      )
    }
  },
  beforeDestroy() {
    this.emptyRequestLoop()
  },
  computed: {
    ...mapState('user', {
      planInfo: 'userPlanInfo',
    }),
    ...mapState({
      isAdBlock: (state) => state.dashboard.isAdBlockEnabled,
    }),
    isAdBlockEnabled() {
      if (this.isAdBlock) {
        return this.isAdBlock
      }
      return false
    },
    retentionLink() {
      return this.getRetentionLink()
    },
    maxRangeDate() {
      return subDays(new Date(), 60)
    },
    videoId() {
      return this.customVideoId
        ? this.customVideoId
        : this.$store.state.video.video.id
    },
    dateRangeValue() {
      return `${format(this.dateRange[0], 'dd/MM/yyyy')} - ${format(
        this.dateRange[1],
        'dd/MM/yyyy'
      )}`
    },
    engagementCalc() {
      let engagementCalc
      let maxPlays = this.totalData.retention
        ? Object.values(this.totalData.retention).reduce((a, b) => {
            return a > b ? a : b
          })
        : 0

      if (
        this.totalData.retention !== {} &&
        this.totalData.retention !== { 0: 0 }
      ) {
        let totalRetention = 0
        for (let el in this.totalData.retention) {
          totalRetention += this.totalData.retention[el]
        }

        engagementCalc =
          ((totalRetention * 5) /
            maxPlays /
            this.$store.state.video.video.length) *
          100
        if (!engagementCalc) engagementCalc = 0
      } else {
        return 0
      }
      return engagementCalc
    },
  },
  methods: {
    ...mapMutations('dashboard', ['UPDATE_IS_ADBLOCK_ENABLED']),
    setVideoCurrentTime(time) {
      this.videoGraph.currentTime = time
    },
    async updateSelect() {
      this.loadings.fetch = true
      await this.updateDataV2()
      this.loadings.fetch = false
    },
    changeUpdateTime() {
      this.emptyRequestLoop()
      this.updateInterval = setInterval(
        this.updateDataV2,
        1000 * this.updateAnalyticsTime.time
      )
    },
    emptyRequestLoop() {
      window.clearInterval(this.updateInterval)
      this.updateInterval = {}
    },
    verifyDisabledDate(date) {
      // 2022-10-04 é quando começou o analytics novo
      if (isBefore(date, new Date('2022-10-04'))) return false
      const canUse =
        isAfter(this.maxRangeDate, date) || !isBefore(date, new Date())
      return canUse
    },
    aggregateRetention(data) {
      if (this.maxValue === 0) {
        if (Math.max(...Object.values(data)) === 0) {
          this.maxValue = 10
        } else {
          this.maxValue = Math.max(...Object.values(data))
        }
      }

      if (Object.keys(data).length <= 1) {
        const videoSize = this.$store.state.video.video.length
        for (let i = 0; i <= videoSize + 5; i += 5) {
          const missingToVideoSize = videoSize - i
          data[i] = 0
          if (missingToVideoSize < 5) {
            data[missingToVideoSize] = 0
            break
          }
        }
      }

      return Object.keys(data)
        .map((item) => ({
          x: item,
          y: data[item],
        }))
        .sort(function (a, b) {
          return a.x - b.x
        })
    },
    async fetchData() {
      this.loadings.fetch = true
      this.datePickerOpen = false
      await this.updateDataV2()
      this.loadings.fetch = false
    },
    updateRetentionChartYaxis(data) {
      this.retentionChartOptions.yaxis = {
        max:
          Math.max(...Object.values(data.retention)) === 0
            ? 10
            : Math.max(...Object.values(data.retention)),
        labels: {
          formatter: (val, i) => {
            if (val) {
              return (i * 10).toFixed(0) + '%'
            }
            return '0%'
          },
        },
      }
    },
    updateRetentionChartYaxisV2(max) {
      this.retentionChartOptions.yaxis = {
        max: Math.max(max) === 0 ? 10 : Math.max(max),
        labels: {
          formatter: (val, i) => {
            if (val) {
              return (i * 10).toFixed(0) + '%'
            }
            return '0%'
          },
        },
      }
    },
    handleClickInput() {
      this.datePickerOpen = true
    },
    async updateDataV2() {
      try {
        const { data } = await analyticsApi.getVideoMetricsV2(
          this.videoId,
          format(this.dateRange[0], 'yyyy-MM-dd'),
          format(this.dateRange[1], 'yyyy-MM-dd')
        )

        this.UPDATE_IS_ADBLOCK_ENABLED(false)

        Object.keys(data.retention).length !== 0
          ? (this.totalData.retention = data.retention)
          : (data.retention = { 0: 0 })

        this.totalData.retention = data.retention

        const getMax = Math.max(
          ...Object.values(data.retention),

          ...Object.values(data.viewsData)
        )

        this.maxValue = Math.max(
          ...Object.values(data.retention),

          ...Object.values(data.viewsData)
        )

        this.retentionChartSeries = [
          {
            name: this.$t('message.pages.video.metricsPage.retentionRate'),
            data: this.aggregateRetention(data.retention),
          },
          {
            name: this.$t('message.pages.video.metricsPage.viewsPerTime'),
            data: this.aggregateRetention(data.viewsData),
          },
        ]

        this.retentionChartOptions.yaxis = [
          {
            max:
              Math.max(getMax) === 0
                ? 10
                : Math.max(...Object.values(data.retention)),
            labels: {
              formatter: (val, i) => {
                if (val) {
                  return (i * 10).toFixed(0) + '%'
                }
                return '0%'
              },
            },
          },
          {
            opposite: true,
            max:
              Math.max(getMax) === 0
                ? 10
                : Math.max(...Object.values(data.viewsData)),
            labels: {
              formatter: (val) => {
                if (val) {
                  if (val > 1000000) {
                    return `${(val / 1000000).toFixed(1)}M`
                  }
                  if (val > 1000) {
                    return `${(val / 1000).toFixed(1)}k`
                  }
                  return val.toFixed(0)
                }
                return '0'
              },
            },
          },
        ]
        this.retentionChartOptions.tooltip = {
          theme: 'dark',
          enabled: true,
          y: {
            formatter: (val, i) => {
              if (i.seriesIndex === 0) {
                return `${((val * 100) / this.maxValue).toFixed(0) + '%'}`
              }
              return val
            },
          },
        }
      } catch (error) {
        const errObject = { error }
        const { response } = errObject
        if (!response?.status) {
          this.UPDATE_IS_ADBLOCK_ENABLED(true)
        }
        console.error('error', error)
        this.errors.fetch = true
      }
    },
  },
}
</script>
<style lang="scss">
.datepicker-container {
  .datepicker-item {
    height: 400px;
    position: absolute;
    top: 80px;
    left: 50px;
    z-index: 2000;
  }
}
.disabled {
  display: none;
}
.date-picker-input {
  width: 100%;
}
</style>
<style scoped>
.chart-container,
.the-retention-chart {
  position: relative;
}

.the-retention-chart {
  z-index: 1;
}

.video-chart-container {
  position: absolute;
  width: 100%;
  top: 20px;
  padding-left: 60px;
  padding-right: 50px;
}
.views-data-container {
  padding-left: 45px;
}

.full-zero-retention {
  padding: 0 53px 0px 4.1%;
}

.full-zero-retention-min-total {
  padding: 0 53px 0px 3%;
}

.video-box {
  position: absolute;
  width: 100%;
  top: 0px;
  height: 302px;
  background: black;
  filter: brightness(0.5);
}
.v-tooltip__content {
  opacity: 0.9 !important;
  background-color: rgb(76, 76, 76) !important;
}
</style>
